/**
 * MappingStatsPopover - Display mapping statistics in a beautiful tooltip
 * 
 * An info button that shows a popover with statistics about data source mappings:
 * - Number of data sources
 * - Areas mapped
 * - Lines mapped  
 * - Field mappings count
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/shared/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import {
  Info,
  Database,
  Layers,
  LayoutList,
  Hash,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

interface MappingStatsPopoverProps {
  factoryId: string;
  tenantId: string;
}

interface MappingStats {
  dataSources: number;
  activeDataSources: number;
  areasWithMappings: number;
  linesWithMappings: number;
  stationsWithMappings: number;
  totalFieldMappings: number;
  totalDatapoints: number;
}

export function MappingStatsPopover({ factoryId, tenantId }: MappingStatsPopoverProps) {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ["mapping-stats", factoryId, tenantId],
    queryFn: async (): Promise<MappingStats> => {
      // Fetch data sources
      const { data: dataSources, error: dsError } = await supabase
        .schema("core")
        .from("data_sources")
        .select("id, is_active")
        .eq("tenant_id", tenantId);
      
      if (dsError) throw dsError;

      // Fetch field mappings with their datapoints
      const { data: fieldMappings, error: fmError } = await supabase
        .schema("core")
        .from("field_mappings")
        .select("id, area_id, line_id, station_id")
        .eq("factory_id", factoryId);
      
      if (fmError) throw fmError;

      // Fetch datapoints
      const { data: datapoints, error: dpError } = await supabase
        .schema("core")
        .from("datapoints")
        .select("id")
        .eq("tenant_id", tenantId);
      
      if (dpError) throw dpError;

      // Calculate unique areas, lines, stations with mappings
      const areasWithMappings = new Set(
        fieldMappings?.filter(m => m.area_id).map(m => m.area_id) || []
      );
      const linesWithMappings = new Set(
        fieldMappings?.filter(m => m.line_id).map(m => m.line_id) || []
      );
      const stationsWithMappings = new Set(
        fieldMappings?.filter(m => m.station_id).map(m => m.station_id) || []
      );

      return {
        dataSources: dataSources?.length || 0,
        activeDataSources: dataSources?.filter(ds => ds.is_active).length || 0,
        areasWithMappings: areasWithMappings.size,
        linesWithMappings: linesWithMappings.size,
        stationsWithMappings: stationsWithMappings.size,
        totalFieldMappings: fieldMappings?.length || 0,
        totalDatapoints: datapoints?.length || 0,
      };
    },
    staleTime: 30000, // 30 seconds
  });

  const hasData = stats && (stats.dataSources > 0 || stats.totalFieldMappings > 0);
  const hasGoodCoverage = stats && stats.activeDataSources > 0 && stats.totalFieldMappings > 5;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Info className="h-4 w-4" />
          Mapping Stats
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : hasGoodCoverage ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : hasData ? (
              <Info className="h-4 w-4 text-chart-2" />
            ) : (
              <AlertCircle className="h-4 w-4 text-warning" />
            )}
            <h4 className="font-semibold">Mapping Overview</h4>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-sm text-destructive text-center py-4">
              Failed to load statistics
            </div>
          ) : (
            <div className="space-y-3">
              {/* Data Sources */}
              <StatRow
                icon={<Database className="h-4 w-4 text-chart-3" />}
                label="Data Sources"
                value={stats!.dataSources}
                subvalue={`${stats!.activeDataSources} active`}
                color="purple"
              />

              {/* Datapoints */}
              <StatRow
                icon={<Hash className="h-4 w-4 text-chart-2" />}
                label="Datapoints"
                value={stats!.totalDatapoints}
                subvalue={`${stats!.totalFieldMappings} mapped`}
                color="cyan"
              />

              {/* Areas */}
              <StatRow
                icon={<Layers className="h-4 w-4 text-chart-2" />}
                label="Areas with Mappings"
                value={stats!.areasWithMappings}
                color="blue"
              />

              {/* Lines */}
              <StatRow
                icon={<LayoutList className="h-4 w-4 text-success" />}
                label="Lines with Mappings"
                value={stats!.linesWithMappings}
                color="green"
              />

              {/* Stations */}
              <StatRow
                icon={<div className="h-4 w-4 flex items-center justify-center text-chart-1 text-xs font-bold">S</div>}
                label="Stations with Mappings"
                value={stats!.stationsWithMappings}
                color="orange"
              />
            </div>
          )}

          {/* Help text */}
          {!isLoading && !error && !hasData && (
            <p className="text-xs text-muted-foreground text-center pt-2 border-t">
              Add data sources and map fields to your factory structure to see statistics here.
            </p>
          )}

          {!isLoading && !error && hasData && !hasGoodCoverage && (
            <p className="text-xs text-muted-foreground text-center pt-2 border-t">
              Continue mapping fields to improve data coverage.
            </p>
          )}

          {!isLoading && !error && hasGoodCoverage && (
            <p className="text-xs text-success text-center pt-2 border-t">
              Good coverage! Your factory structure is well-mapped.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// StatRow Component
// =============================================================================

interface StatRowProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  subvalue?: string;
  color: "purple" | "cyan" | "blue" | "green" | "orange";
}

function StatRow({ icon, label, value, subvalue, color }: StatRowProps) {
  const bgColors = {
    purple: "bg-chart-3/10",
    cyan: "bg-chart-2/10",
    blue: "bg-chart-2/10",
    green: "bg-success/10",
    orange: "bg-chart-1/10",
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bgColors[color]}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        {subvalue && (
          <div className="text-xs text-muted-foreground">{subvalue}</div>
        )}
      </div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}
